import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";
import "../styles/heroBanner.scss";

/**
 * HeroBanner — Shared featured-content hero with trailer auto-play carousel
 * Figma: 24654:4927 / 25460:27096
 *
 * Features:
 *   - Carousel of banners with auto-advancement
 *   - Trailer auto-plays in background (muted) when trailerUrl is provided
 *   - Progress ring on active dot shows trailer playback progress
 *   - Falls back to timed rotation (interval) when no trailer
 *   - Fade transitions between slides
 *   - Click dots to jump to a slide
 *
 * Props:
 *   - banners: Array<{ bgImage, title, titleArt?, rating, rated, year, genres, description, playHref, detailsHref, trailerUrl? }>
 *   - interval: number (ms fallback rotation when no trailer, default 10000)
 *   - showSlider: boolean (show dots, default true)
 *   - Single-banner props (backward compatible fallback)
 */

const DEFAULT_BANNER = {
  bgImage: "/assets/hero/ted-lasso-bg.webp",
  title: "Ted Lasso",
  rating: "9.2",
  rated: "TV-MA",
  year: "2020",
  genres: ["Comedy", "Drama", "Sport"],
  description: "Ted Lasso, an American college football coach, is unexpectedly recruited to coach a fictional English Premier League soccer team, AFC Richmond.",
  playHref: "/player/ted-lasso?type=movie",
  detailsHref: "/details/ted-lasso",
};

const DOT_SIZE = 20;
const DOT_RADIUS = 8;
const DOT_CIRCUMFERENCE = 2 * Math.PI * DOT_RADIUS;

const HeroBanner = ({
  banners,
  interval = 10000,
  showSlider = true,
  loading = false,
  bgImage, title, rating, rated, year, genres, description, playHref, detailsHref, fromTab,
}) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0); // 0–1 for progress ring
  const [trailerPlaying, setTrailerPlaying] = useState(false);
  const [trailerMuted, setTrailerMuted] = useState(true);
  // contentHidden: fades out title/meta/buttons after trailer plays for a few seconds.
  // Mouse movement over the hero reveals the content and resets the idle timer.
  const [contentHidden, setContentHidden] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const lastProgressUpdate = useRef(0);

  // Timings for the immersive-trailer UX
  const AUTO_HIDE_DELAY_MS = 4000; // hide content 4s after trailer starts
  const IDLE_REHIDE_DELAY_MS = 3000; // re-hide 3s after the last mouse move

  // Build slides array
  const slides = banners && banners.length > 0
    ? banners
    : [{
        bgImage: bgImage ?? DEFAULT_BANNER.bgImage,
        title: title ?? DEFAULT_BANNER.title,
        rating: rating ?? DEFAULT_BANNER.rating,
        rated: rated ?? DEFAULT_BANNER.rated,
        year: year ?? DEFAULT_BANNER.year,
        genres: genres ?? DEFAULT_BANNER.genres,
        description: description ?? DEFAULT_BANNER.description,
        playHref: playHref ?? DEFAULT_BANNER.playHref,
        detailsHref: detailsHref ?? DEFAULT_BANNER.detailsHref,
      }];

  const current = slides[activeIndex] || slides[0];

  // Advance to next slide with fade transition
  const advanceSlide = useCallback(() => {
    if (slides.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
      setProgress(0);
      setIsTransitioning(false);
    }, 400);
  }, [slides.length]);

  // Go to specific slide
  const goToSlide = useCallback((index) => {
    if (index === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(index);
      setProgress(0);
      setIsTransitioning(false);
    }, 400);
  }, [activeIndex]);

  // Load and play trailer for current slide
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const trailerUrl = current.trailerUrl;

    // Cleanup previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setTrailerPlaying(false);
    setProgress(0);
    setTrailerMuted(true);
    if (videoRef.current) videoRef.current.muted = true;

    if (!trailerUrl) {
      // No trailer — use fallback timer with progress animation
      video.src = "";
      video.removeAttribute("src");

      let start = Date.now();
      progressTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - start;
        const pct = Math.min(elapsed / interval, 1);
        // Throttle React state updates to every 250ms
        if (pct - lastProgressUpdate.current > 0.025 || pct >= 1) {
          lastProgressUpdate.current = pct;
          setProgress(pct);
        }
        if (pct >= 1) {
          clearInterval(progressTimerRef.current);
          lastProgressUpdate.current = 0;
          advanceSlide();
        }
      }, 50);

      return () => {
        clearInterval(progressTimerRef.current);
      };
    }

    // Has trailer — load via HLS.js or native
    const isM3u8 = trailerUrl.includes(".m3u8");

    if (isM3u8 && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(trailerUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setTrailerPlaying(true);
        console.log("[HeroBanner] Trailer playing:", current.title);
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.warn("[HeroBanner] Trailer HLS error, using fallback timer");
          hls.destroy();
          hlsRef.current = null;
          setTrailerPlaying(false);
          // Fall back to timed progress
          let start = Date.now();
          progressTimerRef.current = setInterval(() => {
            const pct = Math.min((Date.now() - start) / interval, 1);
            if (pct - lastProgressUpdate.current > 0.025 || pct >= 1) {
              lastProgressUpdate.current = pct;
              setProgress(pct);
            }
            if (pct >= 1) { clearInterval(progressTimerRef.current); lastProgressUpdate.current = 0; advanceSlide(); }
          }, 50);
        }
      });
    } else if (isM3u8 && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = trailerUrl;
      video.play().catch(() => {});
      setTrailerPlaying(true);
    } else if (!isM3u8) {
      video.src = trailerUrl;
      video.play().catch(() => {});
      setTrailerPlaying(true);
    }

    return () => {
      clearInterval(progressTimerRef.current);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, slides.length]);

  // Track trailer progress via timeupdate
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !trailerPlaying) return;

    const handleTimeUpdate = () => {
      if (video.duration && video.duration > 0) {
        const pct = video.currentTime / video.duration;
        if (pct - lastProgressUpdate.current > 0.02 || pct >= 0.99) {
          lastProgressUpdate.current = pct;
          setProgress(pct);
        }
      }
    };

    const handleEnded = () => {
      console.log("[HeroBanner] Trailer ended, advancing slide");
      advanceSlide();
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("ended", handleEnded);
    };
  }, [trailerPlaying, advanceSlide]);

  // Cleanup fallback timer on unmount
  useEffect(() => {
    const progressTimer = progressTimerRef;
    const fallbackTimer = fallbackTimerRef;
    const hideTimer = hideTimerRef;
    return () => {
      clearInterval(progressTimer.current);
      clearTimeout(fallbackTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, []);

  // Auto-hide content a few seconds after the trailer starts — immersive mode.
  // Reset on slide change so the next banner's content is visible first.
  useEffect(() => {
    clearTimeout(hideTimerRef.current);
    setContentHidden(false);
    if (!trailerPlaying) return;
    hideTimerRef.current = setTimeout(() => {
      setContentHidden(true);
    }, AUTO_HIDE_DELAY_MS);
    return () => clearTimeout(hideTimerRef.current);
  }, [trailerPlaying, activeIndex]);

  const handleToggleMute = useCallback(() => {
    setTrailerMuted((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  // Reveal content on mouse move, re-hide after idle — only when a trailer is playing.
  const handleHeroMouseMove = useCallback(() => {
    if (!trailerPlaying) return;
    setContentHidden(false);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setContentHidden(true);
    }, IDLE_REHIDE_DELAY_MS);
  }, [trailerPlaying]);

  // Progress ring offset for SVG (dashoffset decreases as progress increases)
  const dashOffset = DOT_CIRCUMFERENCE * (1 - progress);

  console.log("[HeroBanner] rendering:", { title: current.title, slide: `${activeIndex + 1}/${slides.length}`, trailer: trailerPlaying, loading });

  // Loading state — branded spinner on dark bg, same dimensions as hero
  if (loading) {
    return (
      <section className="mango-hero mango-hero--loading">
        <div className="mango-hero__loader">
          <div className="mango-hero__loader-ring" />
        </div>
      </section>
    );
  }

  return (
    <section className="mango-hero" onMouseMove={handleHeroMouseMove}>
      {/* Background — poster image, fades out when trailer plays */}
      <div className={`mango-hero__bg ${isTransitioning ? "mango-hero__bg--fade" : ""}`}>
        <img
          src={current.bgImage}
          alt={current.title || ""}
          className={`mango-hero__bg-img ${trailerPlaying ? "mango-hero__bg-img--hidden" : ""}`}
        />
        {/* Trailer video — plays behind content, muted */}
        <video
          ref={videoRef}
          className={`mango-hero__bg-video ${trailerPlaying ? "mango-hero__bg-video--visible" : ""}`}
          muted={trailerMuted}
          playsInline
          preload="none"
        />
      </div>
      <div className="mango-hero__tint" />
      <div className="mango-hero__gradient-bottom" />
      <div className="mango-hero__gradient-top" />

      <div className="mango-hero__wrapper">
        <div
          className={`mango-hero__content${isTransitioning ? " mango-hero__content--fade" : ""}${contentHidden ? " mango-hero__content--hidden" : ""}`}
        >
          {current.titleArt ? (
            <img
              src={current.titleArt}
              alt={current.title || ""}
              className="mango-hero__title-art"
            />
          ) : (
            <h1 className="mango-hero__show-title">{current.title}</h1>
          )}

          {/* {(current.rating || current.rated || current.year) && (
            <div className="mango-hero__meta">
              {current.rating && (
                <>
                  <span className="mango-hero__rating">
                    <img src="/assets/hero/star-icon.svg" alt="" width="24" height="24" />
                    {current.rating}
                  </span>
                  {(current.rated || current.year) && <span className="mango-hero__divider" />}
                </>
              )}
              {current.rated && (
                <>
                  <span className="mango-hero__badge">{current.rated}</span>
                  {current.year && <span className="mango-hero__divider" />}
                </>
              )}
              {current.year && <span className="mango-hero__badge">{current.year}</span>}
            </div>
          )} */}

          {/* {current.genres && current.genres.length > 0 && (
            <div className="mango-hero__genres">
              {current.genres.map((g) => (
                <span key={g} className="mango-hero__genre">{g}</span>
              ))}
            </div>
          )} */}

          {current.description && <p className="mango-hero__description">{current.description}</p>}

          {(current.playHref || current.detailsHref) && (
            <div className="mango-hero__actions">
              {current.playHref && (
                <button
                  className="mango-hero__play-btn"
                  onClick={() =>
                    navigate(
                      current.playHref,
                      current.playState ? { state: current.playState } : undefined
                    )
                  }
                >
                  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                    <path d="M1 1L13 8L1 15V1Z" fill="#0a0a0a" />
                  </svg>
                  Play
                </button>
              )}
              {current.detailsHref && (
                <button className="mango-hero__details-btn" onClick={() => navigate(current.detailsHref, fromTab ? { state: { fromTab } } : undefined)}>
                  See details
                </button>
              )}
            </div>
          )}
        </div>

        {(showSlider && slides.length > 1) || trailerPlaying ? (
          <div className="mango-hero__slider">
            {showSlider && slides.length > 1 && (
              <div className="mango-hero__dots">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    className={`mango-hero__dot ${i === activeIndex ? "mango-hero__dot--active" : ""}`}
                    onClick={() => goToSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  >
                    {i === activeIndex ? (
                      <svg
                        className="mango-hero__dot-ring"
                        width={DOT_SIZE}
                        height={DOT_SIZE}
                        viewBox={`0 0 ${DOT_SIZE} ${DOT_SIZE}`}
                      >
                        <circle
                          cx={DOT_SIZE / 2}
                          cy={DOT_SIZE / 2}
                          r={DOT_RADIUS}
                          fill="none"
                          stroke="rgba(255,255,255,0.2)"
                          strokeWidth="3"
                        />
                        <circle
                          cx={DOT_SIZE / 2}
                          cy={DOT_SIZE / 2}
                          r={DOT_RADIUS}
                          fill="none"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={DOT_CIRCUMFERENCE}
                          strokeDashoffset={dashOffset}
                          transform={`rotate(-90 ${DOT_SIZE / 2} ${DOT_SIZE / 2})`}
                        />
                      </svg>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
            {trailerPlaying && (
              <button
                type="button"
                className="mango-hero__mute-btn"
                onClick={handleToggleMute}
                aria-label={trailerMuted ? "Unmute trailer" : "Mute trailer"}
              >
                {trailerMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default HeroBanner;
